import { z } from 'zod';

export const SCHEMA_VERSION = 2;
export const DAILY_BUDGET = 60;
export const STAGE_ROUNDS = [3, 3, 2, 2, 1] as const;
export const COOLDOWN_MS = 30 * 60 * 1000;
export const id = z.string().min(1).max(180);
export const studyDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const itemInputSchema = z.object({
  en: z.string().trim().min(1).max(4000),
  zh: z.string().trim().max(8000),
  context: z.string().trim().max(8000).default(''),
});
export const itemSchema = itemInputSchema.extend({
  itemId: id,
  deleted: z.boolean().default(false),
  reactivatedFromItemId: id.optional(),
  starred: z.boolean().default(false),
  wrongCount: z.number().int().nonnegative().default(0),
  correctStreak: z.number().int().nonnegative().default(0),
});
export const categorySchema = z.object({
  categoryId: id,
  name: z.string().trim().min(1).max(100),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  icon: z.string().regex(/^[a-z0-9-]{1,40}$/).default('folder'),
  order: z.number().int(),
  noSchedule: z.boolean().default(false),
});
export const setSchema = z.object({
  setId: id,
  kind: z.enum(['word', 'sentence']),
  title: z.string().max(200),
  categoryId: z.string(),
  noSchedule: z.boolean(),
  deleted: z.boolean(),
  wordEpoch: z.number().int().nonnegative(),
  contentRevision: z.number().int().positive(),
  createdAt: z.number(),
  createdDate: studyDateSchema,
  items: z.array(itemSchema),
  stage: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
  totalRounds: z.number().int().nonnegative(),
  dueDate: studyDateSchema,
  cooldownUntil: z.number().nonnegative(),
  lastAssignedDate: z.string(),
});
export const unitSchema = z.object({
  reviewUnitId: id,
  setId: id,
  kind: z.enum(['word', 'sentence']),
  wordEpoch: z.number().int().nonnegative(),
  contentRevision: z.number().int().positive(),
  itemIds: z.array(id),
  itemCount: z.number().int().positive(),
  stage: z.number().int().nonnegative(),
  ordinal: z.number().int().nonnegative(),
  cooldownUntil: z.number().nonnegative(),
  status: z.enum(['pending', 'completed', 'invalid']),
});
export const planSchema = z.object({
  uid: id,
  studyDate: studyDateSchema,
  budget: z.number().positive(),
  units: z.array(unitSchema),
});
export const snapshotSchema = z.object({
  schemaVersion: z.literal(2),
  uid: id,
  sessionVersion: z.number().int().positive(),
  wordEpoch: z.number().int().nonnegative(),
  nickname: z.string(),
  welcomed: z.boolean(),
  sets: z.array(setSchema),
  categories: z.array(categorySchema),
  historyBaseline: z.number().nonnegative(),
  historyAdded: z.number().nonnegative(),
  dailyHistory: z.record(studyDateSchema, z.number().nonnegative()),
  plans: z.record(studyDateSchema, planSchema),
});
export type ItemInput = z.input<typeof itemInputSchema>;
export type Item = z.infer<typeof itemSchema>;
export type StudySet = z.infer<typeof setSchema>;
export type Category = z.infer<typeof categorySchema>;
export type ReviewUnit = z.infer<typeof unitSchema>;
export type DailyPlan = z.infer<typeof planSchema>;
export type Snapshot = z.infer<typeof snapshotSchema>;
export type Receipt = { request: string; result: MutationResult };
export type Account = Snapshot & {
  receipts: Record<string, Receipt>;
  completedUnits: Record<string, { studyDate: string; count: number; completedAt: number }>;
  deletedAt?: number;
};
export type MutationResult = {
  status: 'applied' | 'duplicate';
  data: Record<string, unknown>;
};
export const mutationBase = {
  schemaVersion: z.literal(2),
  mutationId: id,
  sessionVersion: z.number().int().positive(),
  studyDate: studyDateSchema,
  occurredAt: z.number().int().nonnegative(),
};
export const completeUnitMutationSchema = z
  .object({
    ...mutationBase,
    operation: z.literal('completeUnit'),
    wordEpoch: z.number().int(),
    targetId: id,
    contentRevision: z.number().int().positive(),
    planDate: studyDateSchema.optional(),
  })
  .strict();
export const saveCategoriesMutationSchema = z
  .object({
    ...mutationBase,
    operation: z.literal('saveCategories'),
    payload: z.array(categorySchema).max(100),
    baseCategories: z.array(categorySchema).max(100).optional(),
  })
  .strict();
export const mutationSchema = z.discriminatedUnion('operation', [
  z
    .object({
      ...mutationBase,
      operation: z.literal('addItems'),
      wordEpoch: z.number().int(),
      payload: z.object({
        kind: z.enum(['word', 'sentence']),
        items: z.array(itemInputSchema).min(1).max(500),
        targetSetId: id.optional(),
        contentRevision: z.number().int().positive().optional(),
        categoryId: z.string().default(''),
        title: z.string().max(200).default(''),
      }),
    })
    .strict(),
  z
    .object({
      ...mutationBase,
      operation: z.literal('editItem'),
      wordEpoch: z.number().int(),
      targetId: id,
      contentRevision: z.number().int().positive(),
      payload: itemInputSchema,
    })
    .strict(),
  z
    .object({
      ...mutationBase,
      operation: z.literal('deleteItem'),
      wordEpoch: z.number().int(),
      targetId: id,
      contentRevision: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      ...mutationBase,
      operation: z.literal('deleteSet'),
      wordEpoch: z.number().int(),
      targetId: id,
      contentRevision: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      ...mutationBase,
      operation: z.literal('updateSet'),
      wordEpoch: z.number().int(),
      targetId: id,
      contentRevision: z.number().int().positive(),
      payload: z.object({
        title: z.string().max(200),
        categoryId: z.string(),
        noSchedule: z.boolean(),
      }),
    })
    .strict(),
  completeUnitMutationSchema,
  z
    .object({ ...mutationBase, operation: z.literal('restartWords'), wordEpoch: z.number().int() })
    .strict(),
  z
    .object({
      ...mutationBase,
      operation: z.literal('setStar'),
      wordEpoch: z.number().int(),
      targetId: id,
      contentRevision: z.number().int().positive(),
      payload: z.object({ starred: z.boolean() }),
    })
    .strict(),
  z
    .object({
      ...mutationBase,
      operation: z.literal('quizResult'),
      wordEpoch: z.number().int(),
      targetId: id,
      contentRevision: z.number().int().positive(),
      payload: z.object({
        correct: z.boolean(),
        assisted: z.boolean(),
        disputed: z.boolean(),
        mode: z.enum(['en-zh', 'zh-en', 'listen', 'match', 'spell', 'cloze']),
      }),
    })
    .strict(),
  saveCategoriesMutationSchema,
  z
    .object({
      ...mutationBase,
      operation: z.literal('welcome'),
      payload: z.object({ nickname: z.string().trim().min(1).max(100) }),
    })
    .strict(),
  z.object({ ...mutationBase, operation: z.literal('deleteAccount') }).strict(),
]);
export type Mutation = z.infer<typeof mutationSchema>;

export class DomainError extends Error {
  constructor(
    public code: 'conflict' | 'invalid' | 'unauthorized' | 'temporary',
    message: string,
  ) {
    super(message);
  }
}
export function emptyAccount(uid: string): Account {
  return {
    schemaVersion: 2,
    uid,
    sessionVersion: 1,
    wordEpoch: 0,
    nickname: '',
    welcomed: false,
    sets: [],
    categories: [],
    historyBaseline: 0,
    historyAdded: 0,
    dailyHistory: {},
    plans: {},
    receipts: {},
    completedUnits: {},
  };
}
export function activeSet(account: Pick<Account, 'wordEpoch'>, set: StudySet): boolean {
  return !set.deleted && (set.kind === 'sentence' || set.wordEpoch === account.wordEpoch);
}
export function liveItems(set: StudySet): Item[] {
  return set.items.filter((item) => !item.deleted);
}
export function normalizeWord(value: string): string {
  return value.normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ');
}
export function publicSnapshot(account: Account): Snapshot {
  return snapshotSchema.parse({
    ...account,
    sets: account.sets
      .filter((set) => activeSet(account, set))
      .map((set) => ({ ...set, items: liveItems(set) })),
  });
}
export function canonical(value: unknown): string {
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
  if (value && typeof value === 'object')
    return (
      '{' +
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, val]) => JSON.stringify(key) + ':' + canonical(val))
        .join(',') +
      '}'
    );
  return JSON.stringify(value);
}
